﻿/**
 * 订单详情抽屉 - 主组件（数据获取 / 状态 / 回调编排）
 *
 * 职责：
 * - 通过 /orders/:id/items、/orders/:id、/order-notes/reminders 拉取明细、
 *   完整详情（含 sourceOrder / derivedExpenseOrders 人员调用关联字段）与到期提醒；
 * - 管理本地展示状态（活动信息/时间安排/付款信息编辑、审核、取消等）与全部回调；
 * - UI 区块拆分至 sections/ 目录（受控 props 模式，不持有业务状态），
 *   纯函数与样式映射见 utils.ts，共享类型见 types.ts，通用行组件见 InfoRow.tsx。
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { get, put, patch, upload } from '../../../../api/client';
import { useAuthStore } from '../../../../stores/authStore';
import { handleApiError, isWriteConflict, getSlotDuplicateHint, type SlotDuplicateHintItem } from '../../../../utils/errorHandler';
import { hasNoPaymentObligation, paymentStatusOf, roundMoney, itemsDailySubtotal, hasPaymentRecord } from '../../../../utils/paymentAmount';
import { contactPhoneFormatError } from '../../constants';
import { bookingDays, todayString } from '../../../../utils/dateUtils';
import type { OrderItem } from '../../../../types';
import OrderExportModal from '../export/OrderExportModal';
import ShareOrderModal from '../ShareOrderModal';
import OrderNotes from '../OrderNotes';
import OrderStaffTable from '../OrderStaffTable';
import { calcDuration } from '../order-form-modal/utils';
import SlotDuplicateDialog from '../slot-duplicate-dialog';
import type { Props, OrderItemDetail } from './types';
import { useOrderReminders, useOrderStatusLogs, useOrderCancelRisk, ORDER_STATUS_LOGS_QUERY_KEY } from '../../../../api/orderApi';
import {
  ORDER_STATUS_STYLES,
  PAYMENT_STATUS_STYLES,
  REVIEW_STATUS_STYLES,
  calcScheduleBadge,
  isReviewSettled,
  mapNavigationUrl,
  mapSearchUrl,
} from './utils';
import HeaderSection from './sections/HeaderSection';
import BadgesSection from './sections/BadgesSection';
import MetaInfoSection from './sections/MetaInfoSection';
import AmountCardSection from './sections/AmountCardSection';
import RemindersSection from './sections/RemindersSection';
import ReviewSection from './sections/ReviewSection';
import ReassignSection from './sections/ReassignSection';
import CustomerInfoSection from './sections/CustomerInfoSection';
import ActivityInfoSection from './sections/ActivityInfoSection';
import ScheduleSection from './sections/ScheduleSection';
import SpecialInstructionsSection from './sections/SpecialInstructionsSection';
import OrderItemsSection from './sections/OrderItemsSection';
import PaymentSection from './sections/PaymentSection';
import DerivedExpenseSection from './sections/DerivedExpenseSection';
import StatusLogSection from './sections/StatusLogSection';
import DangerSection from './sections/DangerSection';

export default function OrderDetailDrawer({ order, onClose, onEdit, onCancelled, onUpdated, onNavigateToOrder, onNavigateToCustomer }: Props) {
  const { profile } = useAuthStore();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [orderItems, setOrderItems] = useState<OrderItemDetail[]>([]);
  /** 订单扩展字段（人员调用关联信息）：通过 /orders/:id 拉取 */
  const [sourceOrder, setSourceOrder] = useState<OrderItem['sourceOrder']>(order.sourceOrder ?? null);
  const [sourceStaffRole, setSourceStaffRole] = useState<string | null>(order.sourceStaffRole ?? null);
  const [derivedExpenseOrders, setDerivedExpenseOrders] = useState<NonNullable<OrderItem['derivedExpenseOrders']>>(order.derivedExpenseOrders ?? []);
  const [totalRelatedExpense, setTotalRelatedExpense] = useState<number>(order.totalRelatedExpense ?? 0);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  /**
   * 编辑乐观锁令牌：抽屉手上这份快照的 updatedAt，两处 PUT /orders/:id 都带上它。
   * 付款/审核走各自的独立端点，改的不是同一批字段，不参与这条锁。
   */
  const [lockToken, setLockToken] = useState<string | null>(order.updatedAt);
  useEffect(() => { setLockToken(order.updatedAt); }, [order.updatedAt]);
  const [showExport, setShowExport] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [localReviewStatus, setLocalReviewStatus] = useState(order.reviewStatus);
  /**
   * 审核意见跟着审核态一起本地留一份：拒绝成功后 `order` 那份快照还是旧的（null），
   * 而终态已经不再渲染审核面板，徽标行那句「审核意见」就会是空的——刚点完拒绝却看不见自己填的理由。
   */
  const [localReviewComment, setLocalReviewComment] = useState(order.reviewComment ?? null);
  /**
   * 这两份本地态要跟着 `order` 走，不能只在挂载时播种一次：抽屉是按 id 挂的（父层 `key={modal.order.id}`），
   * 审核完父层重取的是**同一张单**，组件不重挂，于是旧档位会一直留着——
   * 对方在别处审过、或这一笔被服务端改写时，本端仍显「待审核」并把审核入口再给一次，点下去必吃 400。
   * 依赖数组里是原始值，值没变就不会触发重绘，所以自己那一次乐观翻转不会被冲掉。
   */
  useEffect(() => { setLocalReviewStatus(order.reviewStatus); }, [order.reviewStatus]);
  useEffect(() => { setLocalReviewComment(order.reviewComment ?? null); }, [order.reviewComment]);
  /** 审核动作的失败原文：以前只 console.error，页面停在老档位，用户以为「点了没反应」 */
  const [reviewError, setReviewError] = useState('');
  /** 改派客户：候选只在面板首次打开时拉一次，入口本身的开关由后端投影 canReassignCustomer 决定 */
  const [showReassign, setShowReassign] = useState(false);
  const [reassignSearch, setReassignSearch] = useState('');
  const [reassignCandidates, setReassignCandidates] = useState<NonNullable<OrderItem['customer']>[]>([]);
  const [reassignSelectedId, setReassignSelectedId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [reassignError, setReassignError] = useState('');
  /**
   * 疑似重复订单确认弹层：改档期（PUT）与改派客户（PATCH /customer）都会挪档期，
   * 撞车的判定与出口跟新建订单完全同一套，所以要记住命中的是哪条路径。
   */
  const [slotDuplicate, setSlotDuplicate] = useState<{
    path: 'schedule' | 'reassign';
    message: string;
    duplicates: SlotDuplicateHintItem[];
  } | null>(null);
  // 确认标记用 ref 而不是函数入参：区块里是 onClick={onSave}，事件对象会被当成第一个实参传进来
  const confirmedDuplicateRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // 本单的到期提醒：走共享查询键，抽屉里新增/删除备注后这段列表会跟着重取
  const { data: orderReminders = [] } = useOrderReminders({ orderId: order.id });
  const [dismissedReminders, setDismissedReminders] = useState<Set<string>>(new Set());
  /**
   * 状态变更时间线（`GET /orders/:id/status-logs`）。失败原文交给区块渲染，
   * 不在这里兜成空数组——「这单没改过状态」和「没读到它改过没有」必须是两幅画面。
   */
  const statusLogsQuery = useOrderStatusLogs(order.id);
  const queryClient = useQueryClient();

  const [localContactPerson, setLocalContactPerson] = useState(order.contactPerson ?? null);
  const [localContactPhone, setLocalContactPhone] = useState(order.contactPhone ?? null);
  const [localActivityName, setLocalActivityName] = useState(order.activityName ?? null);
  const [localActivityAddress, setLocalActivityAddress] = useState(order.activityAddress ?? null);
  /**
   * 活动地址坐标的本地镜像：库里已有坐标（本端回写过、或别的端落库的）时，
   * 首档导航零请求直接走。必须有自己的镜像而不是每次读 `order`——回写成功后
   * 父层那份快照还是旧的（坐标仍为 null），不镜像就会每次都去打一次地理编码、
   * 每次都回写一次（每次 PUT 还会顶掉乐观锁令牌，见 lockToken 的注释）。
   */
  const [localActivityCoords, setLocalActivityCoords] = useState<{ lat: number; lon: number } | null>(
    order.activityLat != null && order.activityLon != null ? { lat: order.activityLat, lon: order.activityLon } : null
  );
  // 父层重取（别的端落了坐标、或这一单被服务端改写）时镜像跟着走；
  // 依赖数组里是原始值，自己那一次回写不会被冲掉
  useEffect(() => {
    setLocalActivityCoords(
      order.activityLat != null && order.activityLon != null ? { lat: order.activityLat, lon: order.activityLon } : null
    );
  }, [order.activityLat, order.activityLon]);
  const [showActivityEdit, setShowActivityEdit] = useState(false);
  const [editContactPerson, setEditContactPerson] = useState('');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editActivityName, setEditActivityName] = useState('');
  const [editActivityAddress, setEditActivityAddress] = useState('');
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [contactPhoneErr, setContactPhoneErr] = useState('');
  const [activitySuccess, setActivitySuccess] = useState(false);

  const [localStartDate, setLocalStartDate] = useState(order.orderStartDate ?? '');
  const [localEndDate, setLocalEndDate] = useState(order.orderEndDate ?? '');
  const [localIsFullDay, setLocalIsFullDay] = useState(order.isFullDay ?? true);
  const [localTimeStart, setLocalTimeStart] = useState(order.orderTimeStart ?? '');
  const [localTimeEnd, setLocalTimeEnd] = useState(order.orderTimeEnd ?? '');
  const [localArrivalDateTime, setLocalArrivalDateTime] = useState(
    order.arrivalDateTime ? order.arrivalDateTime.slice(0, 16) : ''
  );
  const [showScheduleEdit, setShowScheduleEdit] = useState(false);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editIsFullDay, setEditIsFullDay] = useState(true);
  const [editTimeStart, setEditTimeStart] = useState('');
  const [editTimeEnd, setEditTimeEnd] = useState('');
  const [editArrivalDateTime, setEditArrivalDateTime] = useState('');
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSuccess, setScheduleSuccess] = useState(false);

  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmount, setPayAmount] = useState(String(order.paidAmount ?? ''));
  const [payMethod, setPayMethod] = useState(order.paymentMethod ?? '');
  const [payDate, setPayDate] = useState(order.paymentDate ?? todayString());
  const [payFile, setPayFile] = useState<File | null>(null);
  const [payPreview, setPayPreview] = useState<string | null>(null);
  const [payUploading, setPayUploading] = useState(false);
  const [payError, setPayError] = useState('');
  const [payMethodError, setPayMethodError] = useState('');
  const [paySuccess, setPaySuccess] = useState(false);
  const [localPaidAmount, setLocalPaidAmount] = useState(order.paidAmount);
  const [localVoucherUrl, setLocalVoucherUrl] = useState(order.paymentVoucherUrl);
  const [localPayMethod, setLocalPayMethod] = useState(order.paymentMethod);
  const [localPayDate, setLocalPayDate] = useState(order.paymentDate);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  useEffect(() => {
    get<OrderItemDetail[]>(`/orders/${order.id}/items`).then(data => {
      if (data) {
        setOrderItems(data);
      }
    }).catch(error => {
      console.error('获取订单明细失败:', error);
    });
  }, [order.id]);

  // 拉取订单完整详情（获取 sourceOrder / derivedExpenseOrders / totalRelatedExpense 私有字段）
  useEffect(() => {
    get<OrderItem>(`/orders/${order.id}`).then(data => {
      if (data) {
        setSourceOrder(data.sourceOrder ?? null);
        setSourceStaffRole(data.sourceStaffRole ?? null);
        setDerivedExpenseOrders(data.derivedExpenseOrders ?? []);
        setTotalRelatedExpense(data.totalRelatedExpense ?? 0);
        setLockToken(data.updatedAt ?? null);
      }
    }).catch(error => {
      console.error('获取订单详情失败:', error);
    });
  }, [order.id, detailRefreshKey]);

  const statusStyle = ORDER_STATUS_STYLES[order.orderStatus ?? ''] ?? { bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-300' };
  /**
   * 「已取消」的**展示**判据：审核被拒的单虽然库里也可能落着已取消，它仍要能改回来重提。
   * 只给视觉上需要区分的地方用（付款区块的入口、审核按钮）；编辑类入口一律看 canEdit。
   */
  const isCancelled = order.orderStatus === '已取消' && order.reviewStatus !== '已拒绝';
  /**
   * 编辑类入口（表单编辑、档期/活动信息内联编辑、派工增删、取消）的唯一开关，
   * 由后端 `orderWriteRefusal` 算好下发：建单方 + 档期在途（或被拒豁免）。
   * 过去这里只有一条 `!isCancelled`，于是已完成的单在界面上照样能点进编辑，
   * 提交才吃一个 403；现在入口直接不出。
   */
  const canEdit = order.canEditOrder === true;
  /** 账务与备注的写入归属：只有建单方。客户方能读，但不能往别人的账上写东西 */
  const isOwner = order.isOwner === true;
  const computedPayStatus = useMemo(
    // 判定只有一条：`paymentStatusOf`。0 元单不再映射成「无金额」那句展示文案，
    // 徽章整块撤下（`showPaymentBadge`），抽屉里再没有第四个状态档位。
    () => paymentStatusOf(order.orderAmount, localPaidAmount),
    [order.orderAmount, localPaidAmount],
  );
  const showPaymentBadge = !hasNoPaymentObligation(order.orderAmount, localPaidAmount);
  const payStyle = useMemo(() => {
    return PAYMENT_STATUS_STYLES[computedPayStatus] ?? PAYMENT_STATUS_STYLES['未付款'];
  }, [computedPayStatus]);
  // 每日小计走 web 端唯一实现（与后端 order-amount-guard 同式），抽屉显示的必须是被服务端接受的那个值
  const itemsSubtotalPerDay = useMemo(() => itemsDailySubtotal(orderItems), [orderItems]);
  const numDays = useMemo(
    () => bookingDays(localStartDate, localEndDate),
    [localStartDate, localEndDate],
  );
  // numDays 为 null 表示订单没有预约日期（后端已把它改成必填，只剩历史脏数据）。
  // 「活动天数」那一格显示「-」，金额这里按单日口径不乘天数——乘 0 会把整单明细显示成 0 元。
  const itemsTotal = useMemo(
    () => roundMoney(itemsSubtotalPerDay * (numDays ?? 1)),
    [itemsSubtotalPerDay, numDays],
  );
  const visibleReminders = useMemo(() => {
    return orderReminders.filter(r => !dismissedReminders.has(r.id));
  }, [orderReminders, dismissedReminders]);
  const dismissReminder = (id: string) => setDismissedReminders(prev => new Set(prev).add(id));
  const today = todayString();
  const reviewStyle = useMemo(() => {
    return localReviewStatus ? REVIEW_STATUS_STYLES[localReviewStatus] : null;
  }, [localReviewStatus]);
  const isCustomerFlag = typeof (order as { isCustomerOfOrder?: boolean }).isCustomerOfOrder === 'boolean'
    ? (order as { isCustomerOfOrder: boolean }).isCustomerOfOrder
    : null;
  const isCustomerOfOrder = useMemo(() => {
    // 后端已基于 user_id/customer_id 做了视角判定，前端直接读取
    // 兼容旧逻辑：如后端未下发标识，回退到原 customer.id 判断
    if (isCustomerFlag !== null) return isCustomerFlag;
    return !!(profile?.id && order.customer?.id && order.customer.id === profile.id);
  }, [isCustomerFlag, profile?.id, order.customer?.id]);

  /**
   * 违约风险提示：只在「这一方此刻有得审」时读（判据与窗口/阈值全在服务端 cancelRiskOf）。
   * 读失败不兜成「无风险」——审核面板照旧能点，但会显一句读取失败，
   * 让「没查到」和「查了没问题」是两幅画面。
   */
  const canReviewSlot = isCustomerOfOrder && localReviewStatus === '待审核' && !isCancelled;
  const cancelRiskQuery = useOrderCancelRisk(order.id, canReviewSlot);

  const hasContactPerson = useMemo(() => {
    return !!(localContactPerson?.trim());
  }, [localContactPerson]);
  const hasContactPhone = useMemo(() => {
    return !!(localContactPhone?.trim());
  }, [localContactPhone]);
  const hasActivityName = useMemo(() => {
    return !!(localActivityName?.trim());
  }, [localActivityName]);
  const hasActivityAddress = useMemo(() => {
    return !!(localActivityAddress?.trim());
  }, [localActivityAddress]);
  const hasAnyActivityInfo = useMemo(() => {
    return hasContactPerson || hasContactPhone || hasActivityName || hasActivityAddress;
  }, [hasContactPerson, hasContactPhone, hasActivityName, hasActivityAddress]);

  const displayTimeValue = useMemo(() => {
    return localIsFullDay
      ? '全天'
      : (localTimeStart?.slice(0, 5) ?? '-') + (localTimeEnd ? ' - ' + localTimeEnd.slice(0, 5) : '');
  }, [localIsFullDay, localTimeStart, localTimeEnd]);

  /**
   * 服务时长：库里没有 activity_duration 这一列（投影恒发 null），它是**按起止时间实时算出来的展示值**，
   * 与表单弹窗那一格同源同函数。编辑态另算一份，读用户正在改的值而不是已保存的值——
   * 共用一份就会在改完时段后仍显示旧时长。
   */
  const durationHint = useMemo(
    () => calcDuration(localStartDate, localEndDate, localIsFullDay, localTimeStart, localTimeEnd),
    [localStartDate, localEndDate, localIsFullDay, localTimeStart, localTimeEnd],
  );
  const editDurationHint = useMemo(
    () => calcDuration(editStartDate, editEndDate, editIsFullDay, editTimeStart, editTimeEnd),
    [editStartDate, editEndDate, editIsFullDay, editTimeStart, editTimeEnd],
  );

  const scheduleBadge = useMemo(() => {
    return calcScheduleBadge(localStartDate);
  }, [localStartDate]);

  // 补录订单标记由服务端算好下发（`isBackfilled`）：判定口径是「预约日期早于建单那天」，
  // 客户端自己比日期会在时区上漂，且和下单闸门各算一套
  const isBackfill = order.isBackfilled === true;
  /**
   * 派工（人员调用）入口的方向判据要与服务端同源：服务端闸门只有「`user_id` 为本人 + 非终态」，
   * 完全没有分类条件（order-staff.service.ts），收支方向一律按「分类名含支出」判（utils/order-money.ts）。
   * 端上写精确等值 `=== '收入订单'` 的后果是：自建的不含「支出」二字的收入向分类（「外协服务」「场地」）
   * 服务端允许派工、界面却把入口整块藏掉，功能在这类分类下不可达。
   */
  const isExpenseSideOrder = (order.orderCategory ?? '').includes('支出');

  const handleCancel = async () => {
    setCancelError('');
    setCancelling(true);
    try {
      // 取消 = 置终态「已取消」，走状态收敛端点；订单不能删除
      await patch(`/orders/${order.id}/status`, { status: '已取消' });
      onCancelled();
    } catch (error) {
      // 失败原文留在确认面板里：面板一收、错误只进 console，用户会以为已经取消了
      setCancelError(handleApiError(error).message);
    } finally {
      setCancelling(false);
    }
  };

  const handleApprove = async () => {
    setReviewLoading(true);
    setReviewError('');
    try {
      await put(`/orders/${order.id}/review`, { reviewStatus: '已通过' });
      setLocalReviewStatus('已通过');
      setShowRejectInput(false);
      onUpdated?.();
    } catch (error) {
      // 失败原文留在面板里，档位不动：写成 console.error 就等于「点了没反应」
      setReviewError(handleApiError(error).message);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReject = async () => {
    setReviewLoading(true);
    setReviewError('');
    try {
      await put(`/orders/${order.id}/review`, {
        reviewStatus: '已拒绝',
        reviewComment: rejectReason.trim() || null
      });
      setLocalReviewStatus('已拒绝');
      setLocalReviewComment(rejectReason.trim() || null);
      setShowRejectInput(false);
      setRejectReason('');
      onUpdated?.();
    } catch (error) {
      setReviewError(handleApiError(error).message);
    } finally {
      setReviewLoading(false);
    }
  };

  /** 改派客户候选：与订单表单弹窗同一份来源（GET /customers 已按当前用户的绑定过滤） */
  const handleOpenReassign = () => {
    setShowReassign(true);
    setReassignError('');
    if (reassignCandidates.length > 0) return;
    setReassignLoading(true);
    get<NonNullable<OrderItem['customer']>[]>('/customers').then(data => {
      // 排掉本人与当前客户：改派给同一个人不叫改派，排掉自己则与表单侧同一条约束
      setReassignCandidates((data || []).filter(c => c.id !== profile?.id && c.id !== order.customer?.id));
    }).catch(error => {
      console.error('获取客户列表失败:', error);
      setReassignError('客户列表加载失败，请关闭后重试');
    }).finally(() => setReassignLoading(false));
  };

  const filteredReassignCandidates = useMemo(() => {
    const kw = reassignSearch.trim().toLowerCase();
    if (!kw) return reassignCandidates;
    return reassignCandidates.filter(c =>
      c.fullName?.toLowerCase().includes(kw) || c.companyName?.toLowerCase().includes(kw) || c.phone?.includes(kw));
  }, [reassignCandidates, reassignSearch]);

  const closeReassign = () => {
    setShowReassign(false);
    setReassignError('');
    setReassignSelectedId('');
    setReassignSearch('');
  };

  const handleReassignConfirm = async () => {
    if (!reassignSelectedId) return;
    setReassignSaving(true);
    setReassignError('');
    // 确认标记只随紧接的这一次重发上送：选「是同一订单」之后，下次改派仍要重新经过确认框
    const confirmed = confirmedDuplicateRef.current;
    confirmedDuplicateRef.current = false;
    try {
      await patch<OrderItem>(`/orders/${order.id}/customer`, {
        customer_id: reassignSelectedId,
        ...(confirmed ? { confirm_slot_duplicate: true } : {}),
      });
      // 服务端改派成功即把 approval_status 打回待审核，抽屉里这份本地镜像必须跟着走
      setLocalReviewStatus('待审核');
      setSlotDuplicate(null);
      closeReassign();
      onUpdated?.();
    } catch (e: unknown) {
      // 「疑似重复」不是失败：先转成确认框，别演成一句错误提示 + 一次详情回读
      const hint = getSlotDuplicateHint(e);
      if (hint) {
        setSlotDuplicate({ path: 'reassign', ...hint });
        return;
      }
      const info = handleApiError(e);
      setReassignError(info.message);
      // 「已被他人处理」同样是 409：回读详情，别让用户拿旧快照反复撞
      if (isWriteConflict(e, info)) setDetailRefreshKey(k => k + 1);
    } finally {
      setReassignSaving(false);
    }
  };

  /**
   * 改档期成功后重取时间线：服务端按新档期重算执行状态，这一单可能刚刚多出一行
   * 「未执行 → 已完成」。抽屉是常驻的，react-query 不会自己知道服务端刚写过一行。
   */
  const refreshStatusLogs = () => queryClient.invalidateQueries({ queryKey: ORDER_STATUS_LOGS_QUERY_KEY });

  const handleOpenActivityEdit = () => {
    setEditContactPerson(localContactPerson ?? '');
    setEditContactPhone(localContactPhone ?? '');
    setEditActivityName(localActivityName ?? '');
    setEditActivityAddress(localActivityAddress ?? '');
    setActivityError(''); setContactPhoneErr(''); setShowActivityEdit(true);
  };

  const handleActivitySave = async () => {
    // 对接电话格式闸门挪到 PUT 之前：库里那一列宽 20 字符，什么都能塞，
    // 填成「138-1234」的单在真机上一键拨号就是空号
    const phoneErr = contactPhoneFormatError(editContactPhone);
    setContactPhoneErr(phoneErr);
    if (phoneErr) return;
    setActivitySaving(true);
    setActivityError('');
    try {
      const updated = await put<OrderItem>(`/orders/${order.id}`, {
        contactPerson: editContactPerson.trim() || null,
        contactPhone: editContactPhone.trim() || null,
        activityName: editActivityName.trim() || null,
        activityAddress: editActivityAddress.trim() || null,
        ...(lockToken ? { expectedUpdatedAt: lockToken } : {}),
      });
      if (updated) setLockToken(updated.updatedAt ?? null);
      setLocalContactPerson(editContactPerson.trim() || null);
      setLocalContactPhone(editContactPhone.trim() || null);
      setLocalActivityName(editActivityName.trim() || null);
      setLocalActivityAddress(editActivityAddress.trim() || null);
      // 地址换了，服务端已把旧坐标作废（orders.service 的 activityCoordValue）：
      // 镜像必须跟着清，否则下一次点导航会拿旧坐标把终点导到上一个地点
      if ((editActivityAddress.trim() || null) !== localActivityAddress) setLocalActivityCoords(null);
      setShowActivityEdit(false);
      setActivitySuccess(true);
      onUpdated?.();
      setTimeout(() => setActivitySuccess(false), 3000);
    } catch (e: unknown) {
      const info = handleApiError(e);
      setActivityError(info.message);
      // 撞车说明手上这份快照已过期：回读详情换掉令牌，用户改完再点才可能提交成功
      if (isWriteConflict(e, info)) setDetailRefreshKey(k => k + 1);
    } finally {
      setActivitySaving(false);
    }
  };

  /**
   * 活动地址「导航」：三档行为，与鸿蒙 / Flutter 同源（契约见 docs/接口契约.md）。
   *
   * 窗口必须在用户手势里先占住：地理编码是异步的，等响应回来再 window.open，
   * 浏览器会把它当弹窗拦掉（transient activation 已过期或被消耗）。
   */
  const handleActivityNav = async (address: string) => {
    // 第一档：这一单已经有坐标（本端回写过、或别的端落库的）——零请求直接导航
    if (localActivityCoords) {
      window.open(mapNavigationUrl(localActivityCoords.lat, localActivityCoords.lon, address), '_blank', 'noopener,noreferrer');
      return;
    }
    // 先占一个 about:blank 窗口：它继承 opener 同源，等下能往里写一句「正在定位」，
    // 也能在地理编码回来后用 location.href 导航过去（同步发出的 window.open 才享用户手势）
    const navWindow = window.open('about:blank', '_blank');
    const openInNavWindow = (url: string) => {
      if (navWindow && !navWindow.closed) {
        navWindow.location.href = url;
        return;
      }
      // 窗口被浏览器拦了（极端情况）：退回直接开，用户还能在地址栏右侧放行
      window.open(url, '_blank', 'noopener,noreferrer');
    };
    try {
      navWindow?.document.write('<p style="margin:24px;font:14px system-ui;color:#666">正在定位活动地址…</p>');
    } catch {
      // 个别浏览器给 about:blank 不透明源：写不进去也不影响导航
    }
    let coords: { lat: number; lon: number } | null = null;
    try {
      // 地理编码恒回 200：lat/lon 为 null 就是没解析出来，不是错误
      const result = await get<{ lat: number | null; lon: number | null }>('/geo/geocode', { params: { address } });
      if (result && typeof result.lat === 'number' && typeof result.lon === 'number'
        && Number.isFinite(result.lat) && Number.isFinite(result.lon)) {
        coords = { lat: result.lat, lon: result.lon };
      }
    } catch {
      // 地理编码失败不挡导航：按名称检索的老路照样能走
    }
    if (coords) {
      const landed = coords;
      openInNavWindow(mapNavigationUrl(landed.lat, landed.lon, address));
      // 顺手把坐标落库，下次这一单零请求。失败静默——导航已经出去了，回写只是优化；
      // 令牌跟着回写结果走，否则抽屉里后续的编辑会拿旧令牌撞 409
      void (async () => {
        try {
          const updated = await put<OrderItem>(`/orders/${order.id}`, {
            activityLat: landed.lat,
            activityLon: landed.lon,
            ...(lockToken ? { expectedUpdatedAt: lockToken } : {}),
          });
          if (updated) setLockToken(updated.updatedAt ?? null);
          setLocalActivityCoords(landed);
        } catch {
          // 回写失败（无编辑权、撞车、断网）都不该惊动用户：导航已经完成
        }
      })();
      return;
    }
    // 第三档：网页兜底——装了地图 App 的手机会尝试唤醒它，没装留在网页版
    openInNavWindow(mapSearchUrl(address));
  };

  const handleOpenScheduleEdit = () => {
    setEditStartDate(localStartDate); setEditEndDate(localEndDate);
    setEditIsFullDay(localIsFullDay); setEditTimeStart(localTimeStart);
    setEditTimeEnd(localTimeEnd);
    setEditArrivalDateTime(localArrivalDateTime); setScheduleError(''); setShowScheduleEdit(true);
  };

  const handleScheduleSave = async () => {
    if (!editStartDate) {
      setScheduleError('开始日期不能为空');
      return;
    }
    setScheduleSaving(true);
    setScheduleError('');
    // 起始日在这一格里已是只读的，payload 带的就是库中现值（服务端同值放行、异值 400）；
    // 撞车确认标记仍走同一条出口，与改派共用那一份一次性授权
    const confirmed = confirmedDuplicateRef.current;
    confirmedDuplicateRef.current = false;
    try {
      const updateData: Record<string, unknown> = {
        orderStartDate: editStartDate,
        orderEndDate: editEndDate.trim() || null,
        isFullDay: editIsFullDay,
        orderTimeStart: editIsFullDay ? null : (editTimeStart.trim() || null),
        orderTimeEnd: editIsFullDay ? null : (editTimeEnd.trim() || null),
        // activityDuration 不发：库里没有这一列，时长是前端按起止时间算出来的展示值
        arrivalDateTime: editArrivalDateTime.trim() ? editArrivalDateTime + ':00' : null,
      };
      if (lockToken) updateData.expectedUpdatedAt = lockToken;
      if (confirmed) updateData.confirmSlotDuplicate = true;
      const updated = await put<OrderItem>(`/orders/${order.id}`, updateData);
      if (updated) setLockToken(updated.updatedAt ?? null);
      setLocalStartDate(editStartDate);
      setLocalEndDate(editEndDate.trim() || '');
      setLocalIsFullDay(editIsFullDay);
      setLocalTimeStart(editIsFullDay ? '' : (editTimeStart.trim() || ''));
      setLocalTimeEnd(editIsFullDay ? '' : (editTimeEnd.trim() || ''));
      setLocalArrivalDateTime(editArrivalDateTime.trim() || '');
      setShowScheduleEdit(false);
      setSlotDuplicate(null);
      setScheduleSuccess(true);
      refreshStatusLogs();
      onUpdated?.();
      setTimeout(() => setScheduleSuccess(false), 3000);
    } catch (e: unknown) {
      const hint = getSlotDuplicateHint(e);
      if (hint) {
        setSlotDuplicate({ path: 'schedule', ...hint });
        return;
      }
      const info = handleApiError(e);
      setScheduleError(info.message);
      if (isWriteConflict(e, info)) setDetailRefreshKey(k => k + 1);
    } finally {
      setScheduleSaving(false);
    }
  };

  /** 出口二「不是同一订单」：带确认标记把刚才那次重发一遍（提示那次服务端没落库，改派选择也还在） */
  const resendConfirmedSlotAction = () => {
    if (!slotDuplicate) return;
    confirmedDuplicateRef.current = true;
    if (slotDuplicate.path === 'reassign') void handleReassignConfirm();
    else void handleScheduleSave();
  };

  /** 出口一「是同一订单」：这次写入就此打住，填写内容留着，人去订单审核里处理那张 */
  const abortSlotAction = () => {
    const path = slotDuplicate?.path;
    setSlotDuplicate(null);
    if (path === 'reassign') setReassignError('本次改派尚未生效：请到「订单审核」中核对那张重复订单，或另选一位客户');
    if (path === 'schedule') setScheduleError('本次修改尚未保存：请到「订单审核」中核对那张重复订单');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPayFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPayPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePaySave = async () => {
    setPayError('');
    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt < 0) {
      setPayError('请输入有效的收款金额');
      return;
    }
    // 收了钱就得说清走的什么渠道：这条与订单表单同闸门（#162），
    // 空着方式也能保存的话，#152 那批「已付>0 而方式为空」的单还会继续新增
    const methodErr = amt > 0 && !payMethod.trim() ? '请选择付款方式' : '';
    setPayMethodError(methodErr);
    if (methodErr) return;
    setPayUploading(true);
    try {
      let voucherUrl = localVoucherUrl;
      if (payFile) {
        const result = await upload<{ url: string }>('/uploads/payment-voucher', payFile);
        if (!result?.url) {
          throw new Error('上传凭证失败：服务器未返回文件地址');
        }
        voucherUrl = result.url;
      }
      await put(`/orders/${order.id}/payment`, {
        paidAmount: amt,
        paymentMethod: payMethod.trim() || null,
        paymentDate: payDate || null,
        paymentVoucherUrl: voucherUrl,
      });
      setLocalPaidAmount(amt);
      setLocalVoucherUrl(voucherUrl);
      setLocalPayMethod(payMethod.trim() || null);
      setLocalPayDate(payDate || null);
      setPaySuccess(true);
      setShowPayForm(false);
      setPayFile(null);
      setPayPreview(null);
      onUpdated?.();
      setTimeout(() => setPaySuccess(false), 3000);
    } catch (err: unknown) {
      setPayError(err instanceof Error ? err.message : '保存失败，请重试');
    } finally {
      setPayUploading(false);
    }
  };

  const handleOpenPayForm = () => {
    setPayAmount(String(localPaidAmount ?? ''));
    setPayMethod(localPayMethod ?? '');
    setPayDate(localPayDate ?? todayString());
    setPayFile(null); setPayPreview(null); setPayError(''); setPayMethodError(''); setShowPayForm(true);
  };

  const handleClose = () => { setVisible(false); setTimeout(onClose, 280); };

  return (
    <>
      <div className={`fixed inset-0 bg-black/40 z-50 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`} onClick={handleClose} />
      <div className={`fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 flex flex-col overflow-hidden transition-transform duration-300 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}>

        <HeaderSection
          order={order}
          canEdit={canEdit}
          onEdit={onEdit}
          onClose={handleClose}
          onShowShare={() => setShowShare(true)}
          onShowExport={() => setShowExport(true)}
        />

        <BadgesSection
          order={order}
          statusStyle={statusStyle}
          computedPayStatus={computedPayStatus}
          payStyle={payStyle}
          showPaymentBadge={showPaymentBadge}
          reviewStatus={localReviewStatus}
          reviewStyle={reviewStyle}
          reviewComment={localReviewComment}
          paySuccess={paySuccess}
          activitySuccess={activitySuccess}
          scheduleSuccess={scheduleSuccess}
          localPayMethod={localPayMethod}
          isCustomerOfOrder={isCustomerOfOrder}
          sourceOrder={sourceOrder}
          sourceStaffRole={sourceStaffRole}
          onNavigateToOrder={onNavigateToOrder}
        />

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <MetaInfoSection orderId={order.id} isBackfill={isBackfill} createdAt={order.createdAt} />

          <AmountCardSection
            orderAmount={order.orderAmount}
            serviceTypeName={order.serviceType?.name ?? null}
            paidAmount={localPaidAmount}
            payMethod={localPayMethod}
            itemsTotal={itemsTotal}
          />

          <div className="px-5 py-5 space-y-5">
            {/* Reminders */}
            {visibleReminders.length > 0 && (
              <RemindersSection
                reminders={visibleReminders}
                today={today}
                onDismiss={dismissReminder}
                onDismissAll={() => visibleReminders.forEach(r => dismissReminder(r.id))}
              />
            )}

            {/* Review：终态收敛成徽章行那一枚徽标，这里只留还在等人动作的「待审核」面板。
                已取消的单不再渲染：那张待审邀请随订单作废（后端 findAll 同一口径把
                「待审核 ∧ 已取消」排除在待审之外），面板那句「等待客户方确认」就成了一句假话。 */}
            {localReviewStatus && !isReviewSettled(localReviewStatus) && !isCancelled && (
              <ReviewSection
                order={order}
                reviewStatus={localReviewStatus}
                reviewStyle={reviewStyle}
                isCustomerOfOrder={isCustomerOfOrder}
                isCancelled={isCancelled}
                showRejectInput={showRejectInput}
                rejectReason={rejectReason}
                reviewLoading={reviewLoading}
                reviewError={reviewError}
                onRejectReasonChange={setRejectReason}
                onSetShowRejectInput={setShowRejectInput}
                onApprove={handleApprove}
                onReject={handleReject}
                cancelRisk={cancelRiskQuery.data ?? null}
                cancelRiskFailed={canReviewSlot && cancelRiskQuery.isError}
              />
            )}

            <ReassignSection
              canReassign={order.canReassignCustomer === true}
              currentCustomerName={order.customer?.fullName ?? null}
              open={showReassign}
              search={reassignSearch}
              candidates={filteredReassignCandidates}
              selectedId={reassignSelectedId}
              loading={reassignLoading}
              saving={reassignSaving}
              error={reassignError}
              onOpen={handleOpenReassign}
              onClose={closeReassign}
              onSearchChange={setReassignSearch}
              onPick={setReassignSelectedId}
              onConfirm={handleReassignConfirm}
            />

            {/* Customer info */}
            <CustomerInfoSection customer={order.customer} onOpenCustomer={onNavigateToCustomer} />

            {/* Activity info */}
            <ActivityInfoSection
              localContactPerson={localContactPerson}
              localContactPhone={localContactPhone}
              localActivityName={localActivityName}
              localActivityAddress={localActivityAddress}
              hasContactPerson={hasContactPerson}
              hasContactPhone={hasContactPhone}
              hasActivityName={hasActivityName}
              hasActivityAddress={hasActivityAddress}
              hasAnyActivityInfo={hasAnyActivityInfo}
              showActivityEdit={showActivityEdit}
              canEdit={canEdit}
              editContactPerson={editContactPerson}
              editContactPhone={editContactPhone}
              editActivityName={editActivityName}
              editActivityAddress={editActivityAddress}
              activityError={activityError}
              contactPhoneErr={contactPhoneErr}
              activitySaving={activitySaving}
              onNavigate={handleActivityNav}
              onOpenEdit={handleOpenActivityEdit}
              onCancelEdit={() => { setShowActivityEdit(false); setActivityError(''); setContactPhoneErr(''); }}
              onSave={handleActivitySave}
              onEditContactPersonChange={setEditContactPerson}
              onEditContactPhoneChange={v => { setEditContactPhone(v); setContactPhoneErr(''); }}
              onEditActivityNameChange={setEditActivityName}
              onEditActivityAddressChange={setEditActivityAddress}
            />

            {/* Schedule */}
            <ScheduleSection
              localStartDate={localStartDate}
              localEndDate={localEndDate}
              localIsFullDay={localIsFullDay}
              localTimeStart={localTimeStart}

              durationHint={durationHint}
              localArrivalDateTime={localArrivalDateTime}
              displayTimeValue={displayTimeValue}
              scheduleBadge={scheduleBadge}
              showScheduleEdit={showScheduleEdit}
              canEdit={canEdit}
              editStartDate={editStartDate}
              editEndDate={editEndDate}
              editIsFullDay={editIsFullDay}
              editTimeStart={editTimeStart}
              editTimeEnd={editTimeEnd}
              editDurationHint={editDurationHint}
              editArrivalDateTime={editArrivalDateTime}
              scheduleError={scheduleError}
              scheduleSaving={scheduleSaving}
              onOpenEdit={handleOpenScheduleEdit}
              onCancelEdit={() => { setShowScheduleEdit(false); setScheduleError(''); }}
              onSave={handleScheduleSave}
              onEditEndDateChange={setEditEndDate}
              onEditIsFullDayChange={setEditIsFullDay}
              onEditTimeStartChange={setEditTimeStart}
              onEditTimeEndChange={setEditTimeEnd}
              onEditArrivalDateTimeChange={setEditArrivalDateTime}
            />

            {/* Special instructions - moved here below Schedule */}
            {order.orderSpecialInstructions && (
              <SpecialInstructionsSection instructions={order.orderSpecialInstructions} />
            )}

            {/* Order items */}
            {orderItems.length > 0 && (
              <OrderItemsSection
                items={orderItems}
                itemsSubtotalPerDay={itemsSubtotalPerDay}
                numDays={numDays}
                itemsTotal={itemsTotal}
              />
            )}

            {/* Order Staff (人员调用表) - 仅用户可见且仅收入侧订单，置于付款记录之前（符合录入顺序） */}
            {!isCustomerOfOrder && !isExpenseSideOrder && (
              <div>
                <OrderStaffTable orderId={order.id} canAdd={canEdit} orderCustomerId={order.customer?.id ?? null} onChanged={() => setDetailRefreshKey(k => k + 1)} />
              </div>
            )}

            {/* Payment */}
            <PaymentSection
              orderAmount={order.orderAmount}
              hasPaymentRecord={hasPaymentRecord(localPaidAmount, localPayMethod, localPayDate, localVoucherUrl)}
              isCancelled={isCancelled}
              isOwner={isOwner}
              localPayMethod={localPayMethod}
              localPayDate={localPayDate}
              localPaidAmount={localPaidAmount}
              localVoucherUrl={localVoucherUrl}
              computedPayStatus={computedPayStatus}
              showPayForm={showPayForm}
              payAmount={payAmount}
              payMethod={payMethod}
              payDate={payDate}
              payPreview={payPreview}
              payFile={payFile}
              payError={payError}
              payMethodError={payMethodError}
              payUploading={payUploading}
              fileInputRef={fileInputRef}
              onOpenForm={handleOpenPayForm}
              onCancelForm={() => { setShowPayForm(false); setPayError(''); setPayMethodError(''); setPayFile(null); setPayPreview(null); }}
              onSave={handlePaySave}
              onFileChange={handleFileChange}
              onPayAmountChange={setPayAmount}
              onPayMethodChange={v => { setPayMethod(v); if (v.trim()) setPayMethodError(''); }}
              onPayDateChange={setPayDate}
              onClearFile={() => { setPayFile(null); setPayPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
              onPickFile={() => fileInputRef.current?.click()}
            />

            {/* 人员调用支出核算（仅用户可见且仅收入侧订单存在衍生支出时显示） */}
            {!isCustomerOfOrder && !isExpenseSideOrder && derivedExpenseOrders.length > 0 && (
              <DerivedExpenseSection
                derivedOrders={derivedExpenseOrders}
                totalRelatedExpense={totalRelatedExpense}
                orderAmount={order.orderAmount}
                onNavigateToOrder={onNavigateToOrder}
              />
            )}

            {/* Notes */}
            <div className="pt-2 border-t border-gray-100">
              <div className="pt-3"><OrderNotes orderId={order.id} orderStatus={order.orderStatus} isOwner={isOwner} canEdit={canEdit} /></div>
            </div>

            {/* 状态变更时间线（与 Flutter / hmos 详情页同一端点、同三态） */}
            <StatusLogSection
              logs={statusLogsQuery.data ?? []}
              isLoading={statusLogsQuery.isLoading}
              errorMessage={statusLogsQuery.isError ? handleApiError(statusLogsQuery.error).message : ''}
              onRetry={() => statusLogsQuery.refetch()}
            />

            {/* Danger */}
            {canEdit && (
              <DangerSection
                confirmCancel={confirmCancel}
                cancelling={cancelling}
                error={cancelError}
                onSetConfirmCancel={setConfirmCancel}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>
      </div>
      {slotDuplicate && (
        <SlotDuplicateDialog
          message={slotDuplicate.message}
          duplicates={slotDuplicate.duplicates}
          busy={scheduleSaving || reassignSaving}
          keepLabel={slotDuplicate.path === 'reassign' ? '是同一订单，我去审核处理' : '是同一订单，我放弃这次修改'}
          continueLabel={slotDuplicate.path === 'reassign' ? '不是同一订单，继续改派' : '不是同一订单，继续保存'}
          onKeep={abortSlotAction}
          onContinue={resendConfirmedSlotAction}
        />
      )}
      {showExport && <OrderExportModal order={order} onClose={() => setShowExport(false)} />}
      {showShare && <ShareOrderModal order={order} onClose={() => setShowShare(false)} />}
    </>
  );
}